import React from "react";
import { getInstitute } from "../services/instituteService";
import ExternalLink from "./common/externalLink";
import ReviewCard from "./reviewCard";
import ColoredLine from "./common/coloredLine";
import {
  getAnswers,
  getBinaryAnswers,
  getNumericalAnswers,
} from "../services/answerService";
import { getFields } from "../services/fieldService";
import { sortList } from "../components/common/helpers";
import {
  createReview,
  getReviewByInstituteAndUser,
  getReviewsByInstitute,
  renderReviewAvgsComplete,
} from "../services/reviewService";
import { getUser } from "../services/userService";

import Joi from "joi-browser";
import Form from "./common/form";
import firebase from "../services/firebaseconn";
import _ from "underscore";

// const SCORE_1_5 = "Nota de 1 a 5";
const VALUE_IN_KZ = "Valor em kwanzas";
const VALUE_IN_YEARS = "Valor em anos";
const VALUE_IN_MONTHS = "Valor em meses";
const TEXT_PLACEHOLDER =
  "Escreva alguns conselhos ou experiências positivas e negativas para ajudar quem está interessado a estudar nesta instituição";

const FIELD_LABEL = "Curso";
const PROGRAM_QUALITY_LABEL = "Qualidade do conteúdo programático";
const PROFESSORS_QUALITY_LABEL = "Qualidade de ensino dos professores";
const PROFESSORS_ETHICS_LABEL =
  "Ética profissional, transparência e justa avaliação dos professores";
const ADMINISTRATION_QUALITY_LABEL = "Competência e integridade da direção";
const FACILITIES_QUALITY_LABEL =
  "Qualidade da infra-estrutura (biblioteca, laboratórios, salas de informática, etc...)";
const FACILITIES_HIGIENE_LABEL = "Higiene da infra-estrutura";
const SECURITY_LABEL =
  "Segurança na instituição e na vizinhança da instituição";

const TUTOR_QUALITY_LABEL =
  "Qualidade e acompanhamento do tutor durante a monografia";

const GRADUATION_TIME_LABEL =
  "Tempo médio real de graduação incluindo a defesa";
const DIPLOMA_TIME_LABEL =
  "Tempo médio real de entrega do diploma após a defesa";
const TUITION_LABEL = "Propinas mensais";

const ALLOWS_SEEING_EXAMS_LABEL =
  "Alunos podem sempre ver as próprias provas sem pagar?";
const HAS_INTERNET_LABEL = "Tem internet?";
const HAS_RUNNING_WATER_LABEL = "Tem água corrente?";
const HAS_AC_LABEL = "Salas têm ar-condicionado?";

const HAS_SEXUAL_HARASSMENT_LABEL =
  "Já sofreu ou tem conhecimento de assédio sexual por professores ou funcionários nos últimos dois anos?";
const HAS_CORRUPTION_LABEL =
  "Tem conhecimento de casos de corrupção por professores ou funcionários nos últimos dois anos?";
const HAS_INTERNSHIP_LABEL =
  "Recebeu oportunidade de estágio da instituição ou tem conhecimento de alguém que recebeu?";
const ENDORSEMENT_LABEL =
  "Aconselharia um familiar a estudar nessa instituição?";
const TEXT_LABEL =
  "Fale sobre a sua experiência nesta instituição e/ou deixe sugestões para que a instituição possa melhorar.";

const POST = "Postar";

class InstituteDetails extends Form {
  state = {
    showForm: false,
    currentUser: {},
    reviews: [],
    fields: [],
    institute: {
      id: "",
      name: "",
      abbrev: "",
      type: "",
      country: "",
      province: "",
      town: "",
      streetAddress: "",
      phoneNumbers: [],
      emails: [],
      // photoUris: [], // TODO: consider adding university photos in the future
      website: "",
      description: "",
    },
    data: this.initData(),
    errors: {},
    unsubscribeUser: undefined,
  };

  schema = {
    field: Joi.string().required().label("field"),
    programQuality: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("programQuality"),
    professorsQuality: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("professorsQuality"),
    professorsEthics: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("professorsEthics"),
    administrationQuality: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("administrationQuality"),
    facilitiesQuality: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("facilitiesQuality"),
    facilitiesHigiene: Joi.number()
      .min(1)
      .max(5)
      .required()
      .label("facilitiesHigiene"),
    security: Joi.number().min(1).max(5).required().label("security"),
    tutorQuality: Joi.number()
      .min(1)
      .max(5)
      .allow(null, "")
      .label("tutorQuality"),
    graduationTime: Joi.number().min(0).allow(null, "").label("graduationTime"),
    diplomaTime: Joi.number().min(0).allow(null, "").label("diplomaTime"),
    tuition: Joi.number().min(0).allow(null, "").label("tuition"),

    allowsSeeingExams: Joi.string()
      .valid(Object.keys(getAnswers()))
      .required()
      .label("allowsSeeingExams"),
    hasInternet: Joi.string()
      .valid(Object.keys(getAnswers()))
      .required()
      .label("hasInternet"),
    hasRunningWater: Joi.string()
      .valid(Object.keys(getAnswers()))
      .required()
      .label("hasRunningWater"),
    hasAC: Joi.string()
      .valid(Object.keys(getAnswers()))
      .required()
      .label("hasAC"),
    hasSexualHarassment: Joi.string()
      .valid(Object.keys(getBinaryAnswers()))
      .required()
      .label("hasSexualHarassment"),
    hasCorruption: Joi.string()
      .valid(Object.keys(getBinaryAnswers()))
      .required()
      .label("hasCorruption"),
    hasInternship: Joi.string()
      .valid(Object.keys(getBinaryAnswers()))
      .required()
      .label("hasInternship"),
    endorsement: Joi.string()
      .valid(Object.keys(getBinaryAnswers()))
      .required()
      .label("endorsement"),

    text: Joi.string().allow(null, "").label("text"),
  };

  async componentDidMount() {
    const unsubscribeUser = firebase.auth().onAuthStateChanged(async (user) => {
      if (user && user.email) {
        try {
          const currentUser = await getUser(user.email);
          if (!currentUser) return (window.location = "/logout");

          const id = this.props.match.params.id;
          const institute = await getInstitute(id);
          const reviews = await getReviewsByInstitute(id);
          const fields = await getFields();

          const existingReview = await getReviewByInstituteAndUser(
            id,
            user.email
          );

          const data = existingReview ? existingReview : this.initData();

          this.setState({
            institute: { id: id, ...institute },
            reviews: reviews,
            fields: fields,
            currentUser: currentUser,
            data: data,
          });
        } catch (err) {
          console.log(err.message);
          this.props.history.replace("/not-found");
        }
      } else {
        return (window.location = "/login");
      }
    });
    this.setState({ unsubscribeUser });
  }
  componentWillUnmount() {
    this.state.unsubscribeUser();
  }

  renderContactList(header, list, icon = "") {
    if (!list) return null;
    return (
      <div>
        {/* <h4>{header}</h4> */}
        {list.map((el) => (
          <div className="card-text" key={el}>
            <i className={icon} aria-hidden="true"></i>
            {el}
          </div>
        ))}
      </div>
    );
  }

  initData() {
    return {
      field: "",
      programQuality: "",
      professorsQuality: "",
      professorsEthics: "",
      administrationQuality: "",
      facilitiesQuality: "",
      facilitiesHigiene: "",
      security: "",
      tutorQuality: "",

      graduationTime: "",
      diplomaTime: "",
      tuition: "",

      allowsSeeingExams: "",
      hasInternet: "",
      hasRunningWater: "",
      hasAC: "",

      hasSexualHarassment: "",
      hasCorruption: "",
      hasInternship: "",
      endorsement: "",
      text: "",
    };
  }

  doSubmit = async () => {
    const data = { ...this.state.data };
    const currentUser = { ...this.state.currentUser };
    const institute = { ...this.state.institute };

    if (currentUser) {
      try {
        this.setState({ showForm: false });
        await createReview(data, institute.id, currentUser);
        const reviews = await getReviewsByInstitute(institute.id);

        const updatedInstitute = await getInstitute(institute.id);
        this.setState({
          reviews: reviews,
          institute: updatedInstitute,
        });
      } catch (err) {
        console.log(err.message);
      }
    } else {
      return (window.location = "/login");
    }
  };

  render() {
    const { institute, reviews, currentUser, fields } = this.state;
    const { phoneNumbers, emails, website } = institute;
    const { streetAddress, town, province, country } = institute;

    const title = !institute.abbrev
      ? institute.name
      : `${institute.name} (${institute.abbrev})`;

    return (
      <div className="margin-footer">
        {currentUser.admin ? (
          <button
            className="btn btn-primary"
            onClick={() => {
              this.props.history.push(`/institutes/form/${institute.id}`);
            }}
          >
            Editar
          </button>
        ) : null}

        <h3>{title}</h3>

        <p className="card-text">
          {country + " . " + province + " . " + town + " . " + streetAddress}{" "}
          {website ? " . " : null}
          {<ExternalLink url={`http://${website}`} label={website} />}
        </p>

        <p style={{ whiteSpace: "break-spaces" }}>{institute.description}</p>

        <div style={{ marginTop: 30 }} />

        <div style={{ marginTop: 30 }} />

        <h5>Contactos</h5>
        {this.renderContactList("phoneNumbers", phoneNumbers, "fa fa-phone")}
        {this.renderContactList("emails", emails, "fa fa-envelope")}

        <ColoredLine color="grey" height={1} />

        {institute.reviewAvgs ? <h5>Média das avaliações</h5> : null}
        {renderReviewAvgsComplete(institute.reviewAvgs)}

        <button
          className="btn btn-primary"
          style={{ marginTop: 20, marginBottom: 20 }}
          onClick={() => {
            this.setState({ showForm: true });
          }}
        >
          Avaliar
        </button>

        <ColoredLine color="grey" height={1} />

        {this.state.showForm ? (
          <form onSubmit={this.handleSubmit}>
            {this.renderSelect(
              "field",
              FIELD_LABEL,
              sortList(_.values(fields)),
              false
            )}

            {this.renderSelect(
              "programQuality",
              PROGRAM_QUALITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {/* {this.renderInput(
              "programQuality",
              PROGRAM_QUALITY_LABEL,
              false,
              "number",
              SCORE_1_5
            )} */}

            {this.renderSelect(
              "professorsQuality",
              PROFESSORS_QUALITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "professorsEthics",
              PROFESSORS_ETHICS_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "administrationQuality",
              ADMINISTRATION_QUALITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "facilitiesQuality",
              FACILITIES_QUALITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "facilitiesHigiene",
              FACILITIES_HIGIENE_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "security",
              SECURITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderSelect(
              "tutorQuality",
              TUTOR_QUALITY_LABEL,
              _.values(getNumericalAnswers()),
              false
            )}

            {this.renderInput(
              "graduationTime",
              GRADUATION_TIME_LABEL,
              false,
              "number",
              VALUE_IN_YEARS
            )}
            {this.renderInput(
              "diplomaTime",
              DIPLOMA_TIME_LABEL,
              false,
              "number",
              VALUE_IN_MONTHS
            )}
            {this.renderInput(
              "tuition",
              TUITION_LABEL,
              false,
              "number",
              VALUE_IN_KZ
            )}

            {this.renderSelect(
              "allowsSeeingExams",
              ALLOWS_SEEING_EXAMS_LABEL,
              _.values(getAnswers()),
              false
            )}
            {this.renderSelect(
              "hasInternet",
              HAS_INTERNET_LABEL,
              _.values(getAnswers()),
              false
            )}
            {this.renderSelect(
              "hasRunningWater",
              HAS_RUNNING_WATER_LABEL,
              _.values(getAnswers()),
              false
            )}
            {this.renderSelect(
              "hasAC",
              HAS_AC_LABEL,
              _.values(getAnswers()),
              false
            )}

            {this.renderSelect(
              "hasSexualHarassment",
              HAS_SEXUAL_HARASSMENT_LABEL,
              _.values(getBinaryAnswers()),
              false
            )}
            {this.renderSelect(
              "hasCorruption",
              HAS_CORRUPTION_LABEL,
              _.values(getBinaryAnswers()),
              false
            )}
            {this.renderSelect(
              "hasInternship",
              HAS_INTERNSHIP_LABEL,
              _.values(getBinaryAnswers()),
              false
            )}
            {this.renderSelect(
              "endorsement",
              ENDORSEMENT_LABEL,
              _.values(getBinaryAnswers()),
              false
            )}

            {this.renderTextArea(
              "text",
              TEXT_LABEL,
              false,
              250,
              TEXT_PLACEHOLDER
            )}

            {this.renderButton(POST, false)}
            <button
              className="btn btn-primary"
              style={{ marginBottom: 20, marginLeft: 20 }}
              onClick={(e) => {
                e.preventDefault();
                this.setState({ showForm: false });
              }}
            >
              Cancelar
            </button>
          </form>
        ) : null}

        {reviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            instituteID={institute.id}
            currentUser={currentUser}
            fields={fields}
          />
        ))}
      </div>
    );
  }
}

export default InstituteDetails;
